import cn from "classnames"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
    MixedMerchantDetails,
    PageContainer,
    PageWrapper,
    SpinnerContainer,
    TSwiipePlan,
    getActivePayFac,
    getTextWithInvoicingPrices,
    merchantSelectors,
    useSelector,
} from "swiipe.portal.shared"
import FormHeaderLogoContainer from "../../components/form/title/FormHeaderLogoContainer"
import LegalTextLinkPopup from "../../components/legal/LegalTextLinkPopup"
import CancelService from "../../components/merchant/CancelService"
import GoBackContainer from "../../components/navigations/GoBackContainer"
import PspPricingRows from "../../components/paymentsOnboarding/psp/PspPricingRows"
import OnboardingPricingLine from "../../components/servicesonboarding/OnboardingPricingLine"
import { SharedCancelContainer } from "../../components/servicesonboarding/SharedCancelContainer"
import { getImage } from "../../services/imageLanguageService"
import { navigationService } from "../../services/navigationService"
import { getProvider } from "../../services/paymentService"
import { StoreState } from "../../store/StoreState"
import { billingSelectors } from "../../store/reducers/billingReducer"
import { clearhausSelectors } from "../../store/reducers/clearhausReducer"
import { IWebshopProviderConfig, merchantServicesSelectors } from "../../store/reducers/merchantServicesReducer"
import { cancelOnboardingThunk, getClearhausApplicationDataThunk } from "../../store/thunks/clearhausThunks"
import {
    chooseSwiipePspPlanThunk,
    getOnboardingStatusThunk,
    setPiqOnboardingStatusThunk,
} from "../../store/thunks/swiipePaymentsThunks"
import { sendServiceCancellationEmailThunk } from "../../store/thunks/swiipeServicesThunks"
import { useReduxDispatch } from "../../store/useReduxDispatch"
import "./PspAcquiringDetailsPage.scss"

const PspAcquiringDetailsPage = () => {
    const merchantDetails = useSelector(merchantSelectors.merchantDetails)

    if (!merchantDetails) {
        return null
    }

    return <PspAcquiringDetailsPageInner merchantDetails={merchantDetails} />
}

interface IPspAcquiringDetailsPageInner {
    merchantDetails: MixedMerchantDetails
}

const PspAcquiringDetailsPageInner = ({ merchantDetails }: IPspAcquiringDetailsPageInner) => {
    const { t } = useTranslation()
    const dispatch = useReduxDispatch()
    const swMerchantId = merchantDetails.swMerchant.swMerchantId

    const [showCancelService, setShowCancelService] = useState<"psp" | "acquiring" | undefined>()

    const selectedWebshopId = useSelector(merchantSelectors.selectedWebshopId) ?? ""
    const swiipePlan = useSelector(merchantSelectors.swiipePlan)
    const wProviderConf = useSelector<StoreState, IWebshopProviderConfig | undefined>((state) =>
        merchantServicesSelectors.statuses(state, swMerchantId, selectedWebshopId)
    )

    const activePayFac = getActivePayFac(merchantDetails?.swMerchant.countryCode)
    const applicationData = useSelector((state: StoreState) => clearhausSelectors.getOnboardingData(state, selectedWebshopId))

    const prices = useSelector(billingSelectors.resolvedPrices)

    useEffect(() => {
        dispatch(getOnboardingStatusThunk(swMerchantId, selectedWebshopId, false))
        dispatch(getClearhausApplicationDataThunk(swMerchantId, [selectedWebshopId]))
    }, [swMerchantId, selectedWebshopId])

    const PspPricing = () => {
        if (!swiipePlan) {
            return null
        }

        return (
            <div className="option">
                <div className="main-title">PSP</div>
                <div className="price">
                    <PspPricingRows />
                </div>
                <LegalTextLinkPopup
                    legalTextBeforeLink=""
                    linkText={t("psponboarding.texts.termsForPsp")}
                    termsType="TermsPSP"
                    centered
                />
            </div>
        )
    }

    const AcquiringPricing = () => {
        const acquirerTxFeePctHtml = getTextWithInvoicingPrices("clearhausonboarding.intro.transactionPct", prices, {
            transactionPct: { category: "AcquirerTransactionPct" },
        })

        const acquirerMinTxFee = prices?.find((t) => t.priceType == "AcquirerMinTransactionShared")
        const acquirerTxFee = prices?.find((t) => t.priceType == "CardPaymentTransactionShared")

        let acquirerTxFeeHtml = acquirerTxFeePctHtml
        if (acquirerTxFee != null && acquirerTxFee.amount > 0) {
            const acquirerTxFeeFlatHtml = getTextWithInvoicingPrices("cardpayment.intro.transactionPrice", prices, {
                transactionPrice: { type: "CardPaymentTransactionShared" },
            })
            acquirerTxFeeHtml = `${acquirerTxFeeFlatHtml} + ${acquirerTxFeePctHtml}`
        }

        return (
            <div className="option">
                <div className="main-title">{t("serviceNames.acquirer")}</div>
                <div className="price">
                    <OnboardingPricingLine enlargeSpans priceHtml={acquirerTxFeeHtml} />
                    {acquirerMinTxFee && acquirerMinTxFee.amount > 0 && (
                        <OnboardingPricingLine
                            enlargeSpans
                            priceHtml={getTextWithInvoicingPrices("clearhausonboarding.intro.priceMinimumTx", prices, {
                                acquirerMinTx: { type: "AcquirerMinTransactionShared" },
                            })}
                        />
                    )}
                </div>

                <LegalTextLinkPopup
                    legalTextBeforeLink=""
                    linkText={t("clearhausonboarding.intro.termsAndConditions")}
                    termsType={activePayFac === "Digiteal" ? "TermsPayFaqDigiteal" : "TermsAcquiringClearhaus"}
                    centered
                />
            </div>
        )
    }

    return (
        <>
            {showCancelService === "psp" && (
                <CancelService
                    serviceName={t("psponboarding.texts.pspPlan")}
                    cancelText={t("cancelService.psp.cancelText")}
                    customCancelledText={t("cancelService.psp.cancelledText")}
                    hintText={t("cancelService.psp.hintText")}
                    onCancel={async () => await dispatch(chooseSwiipePspPlanThunk(swMerchantId, swiipePlan, TSwiipePlan.None))}
                    onGoBack={() => setShowCancelService(undefined)}
                />
            )}
            {showCancelService === "acquiring" && (
                <CancelService
                    serviceName={t("serviceNames.cardPayments")}
                    cancelText={t("cancelService.cardPayments.cancelText")}
                    customCancelledText={t("cancelService.cardPayments.cancelledText")}
                    onCancel={async () => {
                        await dispatch(cancelOnboardingThunk(swMerchantId, applicationData?.onboardingId))
                        await dispatch(
                            setPiqOnboardingStatusThunk(swMerchantId, applicationData?.webshopIds || [selectedWebshopId], {
                                paymentType: "CreditCard",
                                providerName: "Clearhaus",
                                providerType: "Aquirer",
                                enabled: false,
                            })
                        )
                        await dispatch(
                            sendServiceCancellationEmailThunk(
                                swMerchantId,
                                "GeneralCardPaymentsCancellationEmail",
                                t("serviceNames.cardPayments")
                            )
                        )
                        await dispatch(
                            getClearhausApplicationDataThunk(
                                swMerchantId,
                                applicationData?.webshopIds || [selectedWebshopId],
                                true
                            )
                        )
                    }}
                    onGoBack={() => setShowCancelService(undefined)}
                />
            )}
            <PageWrapper className={cn({ "d-none": showCancelService })}>
                <div className="go-back-container-psp-acquiring">
                    <GoBackContainer
                        goBackText={t("commononboardingtexts.backToAllService")}
                        onGoBack={() => navigationService.navigate("/plugins")}
                    />
                </div>
                <PageContainer id="psp-acquiring-details-page" transparent noHeader disableDefaultPaddingBottom>
                    <div className="main-container">
                        <FormHeaderLogoContainer
                            mainLogo={{
                                src: getImage("cardPaymentsLogoWithSchemes"),
                                alt: "swiipe-acquiring-logo",
                                width: "235px",
                            }}
                        />
                        <SpinnerContainer showSpinner={!swiipePlan}>
                            <AcquiringPricing />
                            {getProvider("CreditCard", "Clearhaus", wProviderConf?.gatewayProviders)?.enabled && (
                                <SharedCancelContainer
                                    cancelText={t("cancelService.psp.buttonText")}
                                    onCancel={() => setShowCancelService("acquiring")}
                                />
                            )}

                            <hr className="divider" />

                            <PspPricing />
                            {!!swiipePlan && (
                                <SharedCancelContainer
                                    cancelText={t("psponboarding.buttons.cancelPsp")}
                                    onCancel={() => setShowCancelService("psp")}
                                />
                            )}
                        </SpinnerContainer>
                    </div>
                </PageContainer>
            </PageWrapper>
        </>
    )
}

export default PspAcquiringDetailsPage
